import type { RetrievalSource, SourceNode, PdfHighlightAnchor } from './types';

type SourceLike = Pick<RetrievalSource, 'name' | 'source_ref' | 'metadata'> | Pick<SourceNode, 'label' | 'source' | 'metadata'>;
type MetadataRecord = Record<string, unknown>;

export interface StructuredSourceCitation {
  documentName?: string;
  sectionPath: string[];
  sectionPathLabel?: string;
  pageNumbers: number[];
  pageLabel?: string;
  chunkType?: string;
  chunkTypeLabel?: string;
  highlight?: PdfHighlightAnchor;
}

function isOpaqueIdentifier(value: string | undefined): boolean {
  if (!value) {
    return false;
  }

  const normalized = value.trim();
  if (!normalized) {
    return false;
  }

  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalized) ||
    /_doc_[0-9a-f-]{36}_/i.test(normalized) ||
    /_chunk_\d+$/i.test(normalized)
  );
}

function preferHumanReadable(primary: string | undefined, fallback: string | undefined): string | undefined {
  if (primary && !isOpaqueIdentifier(primary)) {
    return primary;
  }

  if (fallback && !isOpaqueIdentifier(fallback)) {
    return fallback;
  }

  return primary ?? fallback;
}

function isRetrievalSourceLike(source: SourceLike): source is Pick<RetrievalSource, 'name' | 'source_ref' | 'metadata'> {
  return 'source_ref' in source || 'name' in source;
}

function resolveSourceDisplayName(source: SourceLike): string | undefined {
  if (isRetrievalSourceLike(source)) {
    return preferHumanReadable(source.source_ref ?? undefined, source.name);
  }

  return preferHumanReadable(source.source ?? undefined, source.label);
}

function asRecord(value: unknown): MetadataRecord | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as MetadataRecord)
    : null;
}

function readString(record: MetadataRecord | null, key: string): string | undefined {
  const value = record?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readStringArray(record: MetadataRecord | null, key: string): string[] {
  const value = record?.[key];
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];
}

function readNumberArray(record: MetadataRecord | null, key: string): number[] {
  const value = record?.[key];
  if (Array.isArray(value)) {
    return value
      .map((entry) => Number(entry))
      .filter((entry) => Number.isFinite(entry));
  }

  if (typeof value === 'number' || typeof value === 'string') {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? [numericValue] : [];
  }

  return [];
}

function firstHumanReadable(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    if (value && !isOpaqueIdentifier(value)) {
      return value;
    }
  }

  return values.find((value) => Boolean(value && value.trim()));
}

function formatPageLabel(pageNumbers: number[]): string | undefined {
  if (pageNumbers.length === 0) {
    return undefined;
  }

  return pageNumbers.length === 1
    ? `S. ${pageNumbers[0]}`
    : `S. ${pageNumbers[0]}-${pageNumbers[pageNumbers.length - 1]}`;
}

export function formatChunkTypeLabel(chunkType: string | undefined): string | undefined {
  if (!chunkType) {
    return undefined;
  }

  const normalized = chunkType.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  switch (normalized) {
    case 'table':
      return 'Tabelle';
    case 'image':
      return 'Bild';
    case 'caption':
      return 'Bildlegende';
    case 'paragraph':
      return 'Absatz';
    case 'header':
      return 'Ueberschrift';
    default:
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
}

function readHighlightAnchor(citation: MetadataRecord | null): PdfHighlightAnchor | undefined {
  const h = asRecord(citation?.highlight);
  if (!h) return undefined;

  // Image-chunk path: bounding-box region takes precedence (no text to search).
  const rawRegion = h?.image_region;
  if (Array.isArray(rawRegion) && rawRegion.length === 4) {
    const coords = rawRegion.map(Number);
    if (coords.every(Number.isFinite)) {
      return { imageRegion: coords as [number, number, number, number] };
    }
  }

  // Text-chunk path: paragraph_id is required.
  const paragraphId = readString(h, 'paragraph_id');
  if (!paragraphId) return undefined;

  const rawRange = h?.char_range;
  let charRange: [number, number] | undefined;
  if (Array.isArray(rawRange) && rawRange.length === 2) {
    const start = Number(rawRange[0]);
    const end = Number(rawRange[1]);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      charRange = [start, end];
    }
  }

  return {
    paragraphId,
    textExcerpt: readString(h, 'text_excerpt'),
    normalizedText: readString(h, 'normalized_text'),
    charRange,
  };
}

export function extractStructuredSourceCitation(source: SourceLike): StructuredSourceCitation {
  const metadata = asRecord(source.metadata);
  const citation = asRecord(metadata?.source_citation);
  const sectionPath = readStringArray(citation, 'section_path');
  const fallbackSectionPath = readStringArray(metadata, 'source_section_path');
  const pageNumbers = readNumberArray(citation, 'page_numbers');
  const fallbackPageNumbers = readNumberArray(metadata, 'source_page_numbers');
  const legacyPageNumbers = [
    ...readNumberArray(citation, 'page_number'),
    ...readNumberArray(metadata, 'page_number'),
    ...readNumberArray(metadata, 'pagenumber'),
  ];
  const resolvedPageNumbers = pageNumbers.length > 0
    ? pageNumbers
    : fallbackPageNumbers.length > 0
      ? fallbackPageNumbers
      : legacyPageNumbers;
  const chunkType = readString(citation, 'chunk_type') ?? readString(metadata, 'source_chunk_type') ?? readString(metadata, 'chunk_type');
  const resolvedSourceDisplayName = resolveSourceDisplayName(source);

  return {
    documentName: firstHumanReadable(
      readString(metadata, 'source_document_name'),
      readString(metadata, 'document_name'),
      readString(citation, 'document_name'),
      resolvedSourceDisplayName,
    ),
    sectionPath: sectionPath.length > 0 ? sectionPath : fallbackSectionPath,
    sectionPathLabel:
      readString(citation, 'section_path_label') ??
      readString(metadata, 'source_section_path_label') ??
      (sectionPath.length > 0 ? sectionPath.join(' > ') : undefined) ??
      (fallbackSectionPath.length > 0 ? fallbackSectionPath.join(' > ') : undefined),
    pageNumbers: resolvedPageNumbers,
    pageLabel:
      readString(citation, 'page_label') ??
      readString(metadata, 'source_page_label') ??
      formatPageLabel(resolvedPageNumbers),
    chunkType,
    chunkTypeLabel: formatChunkTypeLabel(chunkType),
    highlight: readHighlightAnchor(citation),
  };
}

export function buildStructuredSourceLabel(citation: StructuredSourceCitation): string {
  return [citation.documentName, citation.sectionPathLabel, citation.pageLabel, citation.chunkTypeLabel]
    .filter((value): value is string => Boolean(value))
    .join(' • ');
}

export function stringifyMetadataValue(value: unknown): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}